import moment from 'moment'
import Vue from 'vue'

import config from '../conf'

import { parseXmlToJson } from './xml-parser'

const event = new Vue({
  data() {
    return {
      lastURL: '',
    }
  },
  methods: {
    redirectToLastURL(context) {
      context.$router.push({ path: this.lastURL })
    },
  },
})

function colorOverlayCreator(color1) {
  function d2h(d = 0) {
    return d.toString(16)
  }

  function h2d(h) {
    return Number.parseInt(h, 16)
  }

  const weight = 50
  const color2 = '#000000'
  let color = '#'
  for (let index = 1; index <= 5; index += 2) {
    const v1 = h2d(color1.substr(index, 2))
    const v2 = h2d(color2.substr(index, 2))
    let value = d2h(Math.floor(v2 + (v1 - v2) * (weight / 100)))
    while (value.length < 2) {
      value = `0${value}`
    }

    color += value
  }

  return color
}

function locationListSorter(locationList) {
  locationList.sort((locationA, locationB) => {
    // 2000 is international. Want it to be first in the list.
    if (locationA.locationId == 2000) {
      return -1
    }

    if (locationB.locationId == 2000) {
      return 1
    }

    // Sorting by the english name
    return locationA.locationName[0].value.localeCompare(locationB.locationName[0].value)
  })
}

// Loops through each location removes areas which are not countries besides international and capitalizes the first letter of each location name.
function addToListAndRemoveRegionsAndUpperCaseFirstLetter(list, inputList) {
  inputList.forEach((location) => {
    if (location.locationId < 1000 || location.locationId > 1007) {
      location.locationName[0].value =
        location.locationName[0].value.charAt(0).toUpperCase() + location.locationName[0].value.slice(1)
      list.push(location)
    }
  })
}

function getLanguageNameFromId(id, languages) {
  let name = null
  languages.forEach((language) => {
    if (language.languageId === Number(id)) {
      name = language.languageName[0].value
    }
  })

  return name
}

function getLanguageObjectFromId(id, languages) {
  let returnValue = null
  languages.forEach((language) => {
    if (language.languageId === Number(id)) {
      returnValue = language
    }
  })

  return returnValue
}

function getFlagImageUrlByLanguageName() {
  return null
}

function scrollToHash(hashbang) {
  window.location.hash = hashbang
  window.location.href = hashbang
}

function isValidUrl(string) {
  try {
    return Boolean(new URL(string))
  } catch {
    return false
  }
}

function getEpisodeImageURL(episodeImageURL, podcastImageURL, type = 'main') {
  if (type !== 'main' && type !== 'thumb' && type !== 'cover') {
    type = 'main'
  }

  if (isValidUrl(episodeImageURL)) return episodeImageURL

  if (!episodeImageURL || episodeImageURL === podcastImageURL) {
    return config.image.podcast[type] + podcastImageURL
  }

  return config.image.episode[type] + episodeImageURL
}

function urlify(text) {
  const caughtAnchors = []
  const anchorTagMatcher = new RegExp('<a[^>]*>([^<]+)</a>', 'g')
  text = text.replace(anchorTagMatcher, (anchors) =>
    anchors.replace(/rel=".*" /, () => 'rel="noopener noreferrer nofollow" ')
  )
  let match = null
  while ((match = anchorTagMatcher.exec(text))) {
    caughtAnchors.push([match.index, anchorTagMatcher.lastIndex])
  }

  const isBetweenCaughtAnchors = (index) => {
    let isBetween = false
    caughtAnchors.forEach((item) => {
      if (index >= item[0] && index <= item[1]) {
        isBetween = true
      }
    })

    return isBetween
  }

  const urlRegex = /([([]?((http)s?:\/\/|www\.)\S+)/g

  return text.replace(urlRegex, (url) => {
    if (isBetweenCaughtAnchors(text.indexOf(url))) return url
    const urlList = url.replace(/[()[\]]/g, ' ').split(' ')
    for (let index = 0; index < urlList.length; index++) {
      if (urlList[index]) {
        let url = urlList[index]
        if (!/https?:\/\/\S+/.test(url)) {
          url = `https://${url}`
        }

        return `<a href="${url}" rel="noopener noreferrer nofollow" target="_blank">${urlList[index]}</a>`
      }
    }
  })
}

function formatToDefaultTimeFormat(time) {
  return moment(time, 'hh:mm:ss').format('HH:mm')
}

function getUTCtime(unixTimeStamp) {
  return new Date(unixTimeStamp).toUTCString()
}

function getLocalTime(unixTimeStamp, includeTime) {
  return includeTime
    ? new Date(unixTimeStamp).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    : new Date(unixTimeStamp).toLocaleString([], {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
}

function getImageResolution(file) {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image()
      img.addEventListener('load', () => {
        resolve({
          height: img.height,
          width: img.width,
        })
        URL.revokeObjectURL(img.src)
      })
      img.src = URL.createObjectURL(file)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Validate an image file
 *
 * @param {object} file  - File object
 * @param {object} [options={}] - Validation options
 * @param {object} [options.minResolution={}] - Minimum resolution of the image
 * @param {number} [options.minResolution.width=0] - Minimum width of the image
 * @param {number} [options.minResolution.height=0] - Minimum height of the image
 * @param {[number, number]} [options.aspectRatio=[]] - Aspect ratio of the image
 * @returns {[boolean, string]} - An array with the first element being a boolean indicating if the image is valid, and if not, a second element containing the error message
 * @example
 * const [isValidImage, errorMessage] = await validateImage(file, {
 *   minResolution: {
 *     width: 1920,
 *     height: 1080
 *   },
 *   aspectRatio: [16, 9]
 * })
 */
async function validateImage(
  file,
  { minResolution: { height: minHeight = 0, width: minWidth = 0 } = {}, aspectRatio = [] } = {}
) {
  const ASPECT_RATIO_TOLERANCE = 0.001

  if (!file) return [false, 'An image is required.']

  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    return [false, 'Invalid file type. Needs to be a png or jpg.']
  }

  if (!Number.isSafeInteger(minWidth)) {
    Vue.$log.error(`width: ${minWidth} (${typeof minWidth}) is not a safe integer.`)

    return [false]
  }

  if (!Number.isSafeInteger(minHeight)) {
    Vue.$log.error(`height: ${minHeight} (${typeof minHeight}) is not a safe integer.`)

    return [false]
  }

  const { height, width } = await getImageResolution(file)

  if (width < minWidth || height < minHeight) {
    return [false, `Image resolution needs to be at least ${minWidth}x${minHeight}px.`]
  }

  const [dividend, divisor] = aspectRatio
  if ((dividend && !Number.isSafeInteger(dividend)) || (divisor && !Number.isSafeInteger(divisor))) {
    Vue.$log.error(
      `aspectRatio: ${dividend} (${typeof dividend}) / ${divisor} (${typeof divisor}) is not a valid aspect ratio.`
    )

    return [false]
  }

  const imageAspectRatio = width / height
  if (Math.abs(imageAspectRatio - dividend / divisor) > ASPECT_RATIO_TOLERANCE) {
    return [false, `Image aspect ratio needs to be ${dividend}:${divisor}.`]
  }

  return [true]
}

async function uploadImageToS3(file, fileParentObject, fileType, loader, parentId = '') {
  try {
    const { name: fileName, size: fileSize, type: contentType } = file
    const fileExtension = fileName.split('.').pop()
    const { body } = await Vue.http.get(config.upload.image, {
      params: {
        'file-type': contentType,
        'file-ending': fileExtension,
        'file-size': fileSize,
        'file-parent-object': fileParentObject,
        'file-image-type': fileType,
        'file-parent-id': parentId,
      },
    })
    const formData = new FormData()
    body.params.file = file
    loader(true)

    // convert object to formData
    Object.keys(body.params).forEach((key) => formData.append(key, body.params[key]))
    const res = await Vue.http.post(body.endpoint_url, formData)

    const s3URL = parseXmlToJson(res.body).PostResponse.Location
    const s3Key = parseXmlToJson(res.body).PostResponse.Key
    const image = s3Key.split('/')

    loader(false)

    return { ref: image[image.length - 1], url: s3URL, error: null }
  } catch (error) {
    return { ref: null, url: null, error }
  }
}

/**
 * Sort ISO8601 date strings lexicographically
 *
 * @param {object[]} array - Array of objects to sort
 * @param {string} key - Key to sort by
 * @param {('asc'|'desc')} [order='asc'] - Ascending or descending order
 * @returns {object[]} - Sorted clone of array
 * @example
 * const sortedArray = sortByISODateString(array, 'date_created', 'desc')
 */
function sortByISODateString(array = [], key = '', order = 'asc') {
  const arrayClone = structuredClone(array)

  return order === 'asc'
    ? arrayClone.sort((a, b) => a[key].localeCompare(b[key]))
    : order === 'desc'
      ? arrayClone.sort((a, b) => -a[key].localeCompare(b[key]))
      : arrayClone
}

export {
  event,
  scrollToHash,
  colorOverlayCreator,
  locationListSorter,
  addToListAndRemoveRegionsAndUpperCaseFirstLetter,
  getLanguageNameFromId,
  getLanguageObjectFromId,
  getFlagImageUrlByLanguageName,
  isValidUrl,
  getEpisodeImageURL,
  urlify,
  getUTCtime,
  getLocalTime,
  formatToDefaultTimeFormat,
  getImageResolution,
  validateImage,
  uploadImageToS3,
  sortByISODateString,
}



// WEBPACK FOOTER //
// ./src/utils/index.js